import * as T from 'three';
import {PORTAL_DEFAULTS, portalApproachSide, portalEntryFrame, resolvePortalPair, selectedPortals, portalDestinationSide} from './portal-math.mjs';

const FLIP = new T.Matrix4().makeRotationY(Math.PI);
const DEFAULT_WIDTH = PORTAL_DEFAULTS.width, DEFAULT_HEIGHT = PORTAL_DEFAULTS.height;

/** Portal transforms use the floor center as origin and local +Z as front.
 * Rigid mapping is shared with authoritative portal-math.mjs: no player scale.
 */
export function portalFrameMatrix(portal, result = new T.Matrix4()) {
  result.makeRotationY(portal.yaw ?? 0);
  const p = portal.position;
  result.setPosition(p.x, p.y, p.z);
  return result;
}
export function portalCameraMatrix(source, target, cameraMatrix, result = new T.Matrix4()) {
  const inverseSource = portalFrameMatrix(source).invert();
  return result.copy(portalFrameMatrix(target)).multiply(FLIP).multiply(inverseSource).multiply(cameraMatrix);
}

/** Replace the near plane in clip space, retaining only destination geometry
 * beyond the exit. Derived from the standard oblique-near-plane construction.
 * XY projection stays unchanged, which is essential for portal parallax.
 */
export function applyObliquePortalClip(camera, target, side = 1, clipBias = 0.002) {
  const normal = new T.Vector3(Math.sin(target.yaw ?? 0), 0, Math.cos(target.yaw ?? 0)).multiplyScalar(side);
  const origin = new T.Vector3(target.position.x, target.position.y, target.position.z);
  const plane = new T.Plane().setFromNormalAndCoplanarPoint(normal, origin);
  // A tiny push admits the exit frame without leaking geometry behind it.
  plane.constant += clipBias;
  plane.applyMatrix4(camera.matrixWorldInverse);
  const clip = new T.Vector4(plane.normal.x, plane.normal.y, plane.normal.z, plane.constant);
  const projection = camera.projectionMatrix, e = projection.elements;
  const q = new T.Vector4(Math.sign(clip.x) || 1, Math.sign(clip.y) || 1, 1, 1).applyMatrix4(projection.clone().invert());
  const denominator = clip.dot(q);
  if (Math.abs(denominator) < 1e-7 || !Number.isFinite(denominator)) return false;
  clip.multiplyScalar(2 / denominator);
  e[2] = clip.x - e[3];
  e[6] = clip.y - e[7];
  e[10] = clip.z - e[11];
  e[14] = clip.w - e[15];
  camera.projectionMatrixInverse.copy(projection).invert();
  return e.every(Number.isFinite);
}

export function configurePortalCamera(viewCamera, source, target, portalCamera = new T.PerspectiveCamera(), {clip = true, crop = null, sourceSide = null, targetSide = 1} = {}) {
  viewCamera.updateMatrixWorld();
  // Each approached face is its own rigid entry frame. Both look through the
  // same destination front, without reflecting the viewer or its projection.
  const cameraPosition = new T.Vector3().setFromMatrixPosition(viewCamera.matrixWorld);
  const side = sourceSide === -1 || sourceSide === 1 ? sourceSide : portalApproachSide(cameraPosition, source);
  const exitSide = targetSide === -1 ? -1 : 1;
  const entry = portalEntryFrame(source, side);
  portalCamera.matrixAutoUpdate = false;
  portalCamera.matrixWorldAutoUpdate = false;
  portalCamera.matrixWorld.copy(portalCameraMatrix(entry, portalEntryFrame(target, exitSide), viewCamera.matrixWorld));
  portalCamera.matrix.copy(portalCamera.matrixWorld);
  portalCamera.matrixWorld.decompose(portalCamera.position, portalCamera.quaternion, portalCamera.scale);
  portalCamera.matrixWorldInverse.copy(portalCamera.matrixWorld).invert();
  portalCamera.projectionMatrix.copy(viewCamera.projectionMatrix);
  if (crop) cropPortalProjection(portalCamera.projectionMatrix, crop);
  portalCamera.projectionMatrixInverse.copy(portalCamera.projectionMatrix).invert();
  portalCamera.near = viewCamera.near;
  portalCamera.far = viewCamera.far;
  portalCamera.fov = viewCamera.fov;
  portalCamera.aspect = viewCamera.aspect;
  if (clip) applyObliquePortalClip(portalCamera, target, exitSide);
  portalCamera.userData.portalSide = side;
  portalCamera.userData.portalTargetSide = exitSide;
  return portalCamera;
}

/** Restrict an existing (possibly off-axis) projection to a bottom-left UV
 * rectangle. Equivalent to a view offset, without rebuilding the viewer lens.
 * Apply BEFORE oblique clipping: the opposite clip corner must use this frustum.
 */
export function cropPortalProjection(projection, {x, y, width, height}) {
  const crop = new T.Matrix4().set(
    1 / width, 0, 0, (1 - 2 * x - width) / width,
    0, 1 / height, 0, (1 - 2 * y - height) / height,
    0, 0, 1, 0,
    0, 0, 0, 1,
  );
  return projection.premultiply(crop);
}

/** Clip the aperture's conservative rectangle in homogeneous coordinates.
 * Ignoring the ordinary near plane is intentional: the surface shader clamps
 * its depth there, so a doorway must survive right up to the camera crossing.
 * Clipping before perspective division also handles a portal straddling the eye.
 */
export function projectedPortalBounds(portal, camera, bufferWidth, bufferHeight, padding = 2) {
  if (!(bufferWidth > 0 && bufferHeight > 0)) return null;
  const half = (portal.width ?? DEFAULT_WIDTH) / 2, height = portal.height ?? DEFAULT_HEIGHT;
  const matrix = new T.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse).multiply(portalFrameMatrix(portal));
  let polygon = [[-half, 0], [half, 0], [half, height], [-half, height]].map(([x, y]) => new T.Vector4(x, y, 0, 1).applyMatrix4(matrix));
  const planes = [p => p.w - 1e-6, p => p.x + p.w, p => p.w - p.x, p => p.y + p.w, p => p.w - p.y, p => p.w - p.z];
  for (const distance of planes) {
    const clipped = [];
    for (let i = 0; i < polygon.length; i++) {
      const a = polygon[i], b = polygon[(i + 1) % polygon.length], da = distance(a), db = distance(b);
      if (da >= 0) clipped.push(a);
      if ((da >= 0) !== (db >= 0)) clipped.push(a.clone().lerp(b, da / (da - db)));
    }
    polygon = clipped;
    if (polygon.length < 3) return null;
  }
  const points = polygon.map(p => new T.Vector2((p.x / p.w + 1) * bufferWidth / 2, (p.y / p.w + 1) * bufferHeight / 2));
  const area = Math.abs(points.reduce((sum, a, i) => { const b = points[(i + 1) % points.length]; return sum + a.x * b.y - b.x * a.y; }, 0)) / 2;
  if (!Number.isFinite(area) || area < 1) return null;
  const left = Math.max(0, Math.floor(Math.min(...points.map(p => p.x)) - padding));
  const bottom = Math.max(0, Math.floor(Math.min(...points.map(p => p.y)) - padding));
  const right = Math.min(bufferWidth, Math.ceil(Math.max(...points.map(p => p.x)) + padding));
  const top = Math.min(bufferHeight, Math.ceil(Math.max(...points.map(p => p.y)) + padding));
  const pixelWidth = right - left, pixelHeight = top - bottom;
  if (pixelWidth < 1 || pixelHeight < 1) return null;
  return {x: left / bufferWidth, y: bottom / bufferHeight, width: pixelWidth / bufferWidth, height: pixelHeight / bufferHeight, pixelWidth, pixelHeight, area};
}

/** Stable 64-pixel allocations with grow-on-demand and delayed large shrinks. */
export function portalTargetSize(bounds, {scale = 1, maxResolution = 2048, previous = null, time = 0, lastResize = -Infinity} = {}) {
  const density = Math.min(scale, maxResolution / Math.max(bounds.pixelWidth, bounds.pixelHeight));
  const bucket = value => Math.min(maxResolution, Math.max(2, Math.ceil(value / 64) * 64));
  const width = bucket(bounds.pixelWidth * density), height = bucket(bounds.pixelHeight * density);
  if (!previous) return {width, height};
  const shrink = time - lastResize > 1 && width * height < previous.width * previous.height * .55;
  return {width: shrink ? width : Math.max(previous.width, width), height: shrink ? height : Math.max(previous.height, height)};
}

/** Persistent framebuffer storage, independent of per-frame raster dimensions.
 * The largest aperture gets the first slot; smaller slots share the remainder.
 * RGBA16F costs 8 bytes/pixel, depth is conservatively estimated as 4 bytes.
 * Multisampling retains resolved color plus multisampled color/depth storage.
 */
export function portalPoolCapacities(bufferWidth, bufferHeight, {count = 3, maxResolution = 2048, maxStoragePixels = 6 * 1024 * 1024, maxStorageBytes = 80 * 1024 * 1024, samples = 0, viewCount = 1} = {}) {
  if (viewCount > 1) {
    // Left/right views of one doorway need equal fidelity. Dividing a shared
    // budget per eye avoids the largest-slot policy starving the second eye.
    return portalPoolCapacities(bufferWidth, bufferHeight, {count, maxResolution, maxStoragePixels: maxStoragePixels / viewCount, maxStorageBytes: maxStorageBytes / viewCount, samples})
      .flatMap(capacity => Array.from({length: viewCount}, () => ({...capacity})));
  }
  const bytesPerPixel = samples > 0 ? 8 + samples * 12 : 12;
  let remaining = Math.max(4 * count, Math.floor(Math.min(maxStoragePixels, maxStorageBytes / bytesPerPixel)));
  const fullWidth = Math.max(2, Math.min(maxResolution, Math.ceil(bufferWidth / 64) * 64));
  const fullHeight = Math.max(2, Math.min(maxResolution, Math.ceil(bufferHeight / 64) * 64));
  const capacities = [];
  for (let i = 0; i < count; i++) {
    const slots = count - i, budget = i === 0 ? remaining - (slots - 1) * 4 : Math.floor(remaining / slots);
    const density = Math.min(1, Math.sqrt(Math.max(4, budget) / (fullWidth * fullHeight)));
    const width = Math.max(2, Math.floor(fullWidth * density / 64) * 64);
    const height = Math.max(2, Math.floor(fullHeight * density / 64) * 64);
    capacities.push({width, height, bytes: width * height * bytesPerPixel});
    remaining -= width * height;
  }
  return capacities;
}

export function commonPortalAperture(source, target = source) {
  return {
    width: Math.min(source.width ?? DEFAULT_WIDTH, target.width ?? DEFAULT_WIDTH),
    height: Math.min(source.height ?? DEFAULT_HEIGHT, target.height ?? DEFAULT_HEIGHT),
    aperture: source.aperture === 'arch' || target.aperture === 'arch' ? 'arch' : 'rectangle',
  };
}
function apertureShape({width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT, aperture = 'rectangle'}) {
  const halfWidth = width / 2, shape = new T.Shape();
  shape.moveTo(-halfWidth, 0);
  shape.lineTo(halfWidth, 0);
  if (aperture === 'arch') {
    const radius = Math.min(halfWidth, height), spring = height - radius;
    shape.lineTo(halfWidth, spring);
    // An elliptical cap also supports explicitly short, wide openings.
    shape.absellipse(0, spring, halfWidth, radius, 0, Math.PI, false, 0);
  } else { shape.lineTo(halfWidth, height); shape.lineTo(-halfWidth, height); }
  shape.closePath();
  return shape;
}
export function createPortalApertureGeometry(portal) {
  const geometry = new T.ShapeGeometry(apertureShape(portal), 32);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function createPortalFrame(portal) {
  const dimensions = {width: portal.width ?? DEFAULT_WIDTH, height: portal.height ?? DEFAULT_HEIGHT, aperture: portal.aperture};
  const group = new T.Group();
  group.name = `Doorway frame · ${portal.id}`;
  group.matrixAutoUpdate = false;
  group.matrix.copy(portalFrameMatrix(portal));
  const stone = new T.MeshStandardMaterial({color: '#bfc7d2', metalness: .36, roughness: .45});
  const metal = new T.MeshStandardMaterial({color: '#29354c', metalness: .64, roughness: .29});
  const glow = portal.frame === 'doorway' ? null : new T.MeshStandardMaterial({color: '#72c6d8', emissive: '#4ba9c7', emissiveIntensity: .8, roughness: .32});
  const add = (w, h, d, x, y, material) => {
    const mesh = new T.Mesh(new T.BoxGeometry(w, h, d), material);
    mesh.position.set(x, y, 0); mesh.castShadow = true; mesh.receiveShadow = true; group.add(mesh); return mesh;
  };
  const half = dimensions.width / 2, height = dimensions.height;
  if (dimensions.aperture === 'arch') {
    const radius = Math.min(half, height), spring = height - radius;
    for (const side of [-1, 1]) add(.25, spring, .42, side * (half + .125), spring / 2, stone);
    const arch = new T.Mesh(new T.TorusGeometry(half + .125, .125, 8, 48, Math.PI), stone);
    arch.position.y = spring; arch.scale.y = (radius + .125) / (half + .125); group.add(arch);
  } else {
    for (const side of [-1, 1]) {
      add(.25, height + .23, .48, side * (half + .125), (height + .23) / 2, stone);
      if (portal.frame !== 'doorway') {
        const strip = add(.035, height - .2, .51, side * (half + .035), height / 2, glow);
        strip.castShadow = false;
      }
    }
    add(dimensions.width + .5, .23, .48, 0, height + .115, metal);
  }
  // The world owns the walkable floor. A threshold whose top is exactly at
  // the doorway origin competes with authored paving at that same height.
  // Leave the opening floor clear rather than adding a coplanar cover plate.
  return group;
}
function disposeObject(root) {
  const geometries = new Set(), materials = new Set();
  root.traverse(object => {
    if (object.geometry) geometries.add(object.geometry);
    for (const material of object.material ? (Array.isArray(object.material) ? object.material : [object.material]) : []) materials.add(material);
  });
  geometries.forEach(item => item.dispose());
  materials.forEach(item => item.dispose());
  root.removeFromParent();
}

function createStatusLabel(portal) {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas'); canvas.width = 512; canvas.height = 64;
  const context = canvas.getContext('2d'); if (!context) return null;
  const texture = new T.CanvasTexture(canvas); texture.colorSpace = T.SRGBColorSpace;
  const label = new T.Sprite(new T.SpriteMaterial({map: texture, transparent: true, depthWrite: false, toneMapped: false}));
  label.name = `Doorway connection status · ${portal.id}`;
  label.position.set(portal.position.x, portal.position.y + (portal.height ?? DEFAULT_HEIGHT) + .5, portal.position.z);
  label.scale.set(3.2, .4, 1);
  label.userData.setStatus = status => {
    if (label.userData.status === status) return;
    label.userData.status = status; label.visible = status !== 'ready';
    context.clearRect(0, 0, 512, 64);
    context.textAlign = 'center'; context.textBaseline = 'middle'; context.font = '500 24px system-ui';
    context.fillStyle = status === 'unavailable' ? '#e2b598' : '#c2dbe5';
    context.fillText(status === 'unavailable' ? 'CONNECTION UNAVAILABLE' : 'CONNECTING…', 256, 32);
    texture.needsUpdate = true;
  };
  label.userData.dispose = () => { texture.dispose(); label.material.dispose(); label.removeFromParent(); };
  label.userData.setStatus('loading');
  return label;
}
function setStatus(item, status) { item.status = status; item.label?.userData.setStatus(status); }

/** Resolve the current stereo poses before prepasses. The main XR bridge owns
 * the camera anchor and unit conversion; its eye list must match the main draw.
 * With no tracked pose yet, draw no membrane rather than reusing a mono image.
 */
const portalEyeCameras = new WeakMap();
export function portalViewCameras(renderer, camera) {
  if (!renderer.xr?.enabled || !renderer.xr.isPresenting) return [camera];
  const bridge = renderer.userData?.valoriaXR;
  const views = typeof bridge?.getViewCameras === 'function'
    ? bridge.getViewCameras(camera) : renderer.xr.getCamera?.()?.cameras;
  return (Array.isArray(views) ? views : []).filter(view => view?.isPerspectiveCamera).map((view, index) => {
    // XR eyes have a local matrix relative to the bridge rig without an Object3D
    // parent. Calling updateMatrixWorld on that object would erase its anchor.
    let copy = portalEyeCameras.get(view);
    if (!copy) { copy = new T.PerspectiveCamera(); portalEyeCameras.set(view, copy); }
    copy.matrixAutoUpdate = false; copy.matrixWorldAutoUpdate = false;
    copy.matrix.copy(view.matrixWorld); copy.matrixWorld.copy(view.matrixWorld);
    copy.matrixWorld.decompose(copy.position, copy.quaternion, copy.scale);
    copy.matrixWorldInverse.copy(copy.matrixWorld).invert();
    copy.projectionMatrix.copy(view.projectionMatrix);
    copy.projectionMatrixInverse.copy(view.projectionMatrix).invert();
    copy.near = view.near; copy.far = view.far; copy.fov = view.fov; copy.aspect = view.aspect;
    copy.layers.mask = view.layers.mask;
    copy.viewport = view.viewport?.clone();
    copy.userData.valoriaPortalEyeIndex = index;
    return copy;
  });
}
export function portalViewSize(camera, fallbackWidth, fallbackHeight) {
  const viewport = camera.viewport;
  return {width: viewport?.z > 0 ? viewport.z : fallbackWidth, height: viewport?.w > 0 ? viewport.w : fallbackHeight};
}
const eyeSamples = new WeakMap();
function applyPortalEyeSample(material, sample) {
  const uniforms = material.uniforms;
  uniforms.portalSampleReady.value = sample ? 1 : 0;
  if (sample) {
    const {texture, bounds, width, height, targetWidth, targetHeight} = sample;
    uniforms.destination.value = texture;
    uniforms.screenCrop.value.set(bounds.x, bounds.y, bounds.width, bounds.height);
    uniforms.targetUVScale.value.set(width / targetWidth, height / targetHeight);
    uniforms.targetUVBounds.value.set(.5 / targetWidth, .5 / targetHeight, (width - .5) / targetWidth, (height - .5) / targetHeight);
  }
  // A material is shared by both eye draws. Three may retain its program and
  // material cache, so explicitly upload the changed sampler/crop every time.
  material.uniformsNeedUpdate = true;
}
/** Install on either ordinary or SnowJam projective portal ShaderMaterials. */
export function installPortalEyeSampling(surface) {
  if (eyeSamples.has(surface)) return surface;
  const material = surface.material;
  if (!material?.isShaderMaterial || !/void\s+main\s*\(\s*\)\s*\{/.test(material.fragmentShader)) throw new TypeError('Projective portal ShaderMaterial required');
  material.uniforms.portalSampleReady = {value: 0};
  material.fragmentShader = `uniform float portalSampleReady;\n${material.fragmentShader.replace(/void\s+main\s*\(\s*\)\s*\{/, '$&if(portalSampleReady<.5)discard;')}`;
  material.needsUpdate = true;
  const state = {cameras: new Map(), eyes: new Map(), mono: null};
  eyeSamples.set(surface, state);
  const previous = surface.onBeforeRender;
  surface.onBeforeRender = function(renderer, scene, camera, ...rest) {
    previous?.call(this, renderer, scene, camera, ...rest);
    const activeEyes = renderer.xr?.enabled && renderer.xr.isPresenting ? renderer.xr.getCamera?.()?.cameras : null;
    const actualIndex = activeEyes?.indexOf(camera) ?? -1;
    const eyeIndex = actualIndex >= 0 ? actualIndex : camera.userData?.valoriaPortalEyeIndex;
    const sample = state.cameras.get(camera) ?? (Number.isInteger(eyeIndex) ? state.eyes.get(eyeIndex) : state.mono);
    applyPortalEyeSample(material, sample);
  };
  return surface;
}
export function resetPortalEyeSamples(surface) {
  const state = eyeSamples.get(surface);
  if (!state) throw new TypeError('Portal eye sampling must be installed');
  state.cameras.clear(); state.eyes.clear(); state.mono = null;
  applyPortalEyeSample(surface.material, null);
}
export function setPortalEyeSample(surface, camera, sample) {
  const state = eyeSamples.get(surface);
  if (!state) throw new TypeError('Portal eye sampling must be installed');
  state.cameras.set(camera, sample);
  const eyeIndex = camera.userData?.valoriaPortalEyeIndex;
  if (Number.isInteger(eyeIndex)) state.eyes.set(eyeIndex, sample);
  else state.mono = sample;
  applyPortalEyeSample(surface.material, sample);
}

function createSurface(portal) {
  const material = new T.ShaderMaterial({
    name: 'Live world portal · projective view',
    side: T.DoubleSide, depthWrite: true, depthTest: true, transparent: false,
    uniforms: {destination: {value: null}, screenCrop: {value: new T.Vector4(0, 0, 1, 1)}, targetUVScale: {value: new T.Vector2(1, 1)}, targetUVBounds: {value: new T.Vector4(0, 0, 1, 1)}},
    vertexShader: `varying vec4 screenPosition;
      void main(){gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);screenPosition=gl_Position;
        gl_Position.z=max(gl_Position.z,-gl_Position.w+.00001);}`,
    fragmentShader: `uniform sampler2D destination;uniform vec4 screenCrop;uniform vec2 targetUVScale;uniform vec4 targetUVBounds;varying vec4 screenPosition;
      void main(){vec2 screenUV=screenPosition.xy/screenPosition.w*.5+.5;
        vec2 portalUV=(screenUV-screenCrop.xy)/screenCrop.zw;
        gl_FragColor=vec4(texture2D(destination,clamp(portalUV*targetUVScale,targetUVBounds.xy,targetUVBounds.zw)).rgb,1.);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  });
  const surface = installPortalEyeSampling(new T.Mesh(createPortalApertureGeometry(portal), material));
  surface.name = `Live portal · ${portal.id} → ${portal.targetWorldId}`;
  surface.matrixAutoUpdate = false;
  surface.matrix.copy(portalFrameMatrix(portal));
  surface.visible = false;
  surface.frustumCulled = false; // Our homogeneous aperture clip includes the near-plane depth clamp.
  surface.userData.isPortalSurface = true;
  surface.userData.portalId = portal.id;
  return {portal, surface, status: 'loading', label: createStatusLabel(portal), camera: new T.PerspectiveCamera(), apertureKey: ''};
}

function supportedPortalSamples(renderer, requested) {
  // Float color and depth attachment support can differ from MAX_SAMPLES.
  const gl = renderer.getContext?.();
  if (!gl?.getInternalformatParameter || requested < 1) return 0;
  try {
    const color = gl.getInternalformatParameter(gl.RENDERBUFFER, gl.RGBA16F, gl.SAMPLES);
    const depth = gl.getInternalformatParameter(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, gl.SAMPLES);
    return Math.max(0, ...Array.from(color ?? []).filter(value => value <= requested && Array.from(depth ?? []).includes(value)));
  } catch { return 0; }
}

/** Retain the actual programs requested by this compile. Three r185's
 * compileAsync polls material.currentProgram, which later renders/compiles can
 * replace while an earlier HDR/display variant is still compiling. Capture all
 * cached variants for each requested material, including transparent front/back
 * passes and shared-material geometry variants. Never retain mutable properties.
 * This is readiness for compiled main-pass programs only; real warm draws still
 * cover material-local clipping, buffers and shadow depth/distance resources.
 */
export function compilePortalProgramsReady(renderer, scene, camera, targetScene = null) {
  // Compatibility for alternate renderers. The installed WebGLRenderer exposes
  // both APIs below; its supported path does not use mutable compileAsync polls.
  if (typeof renderer.compile !== 'function' || typeof renderer.properties?.get !== 'function') {
    return renderer.compileAsync(scene, camera, targetScene);
  }
  const materials = renderer.compile(scene, camera, targetScene), programs = new Set();
  for (const material of materials) {
    const properties = renderer.properties.get(material);
    const variants = properties.programs?.values?.();
    if (variants) for (const program of variants) programs.add(program);
    if (properties.currentProgram) programs.add(properties.currentProgram);
  }
  return new Promise((resolve, reject) => {
    let failure;
    const check = () => {
      for (const program of programs) {
        try {
          if (typeof program?.isReady !== 'function') throw Error('Unsupported WebGL program readiness interface');
          if (program.isReady()) programs.delete(program);
        } catch (error) { failure ??= error; programs.delete(program); }
      }
      // A failed record must not release materials still owned by another poll.
      if (!programs.size) { if (failure) reject(failure); else resolve(scene); return; }
      setTimeout(check, 10);
    };
    // Without KHR_parallel_shader_compile, Three reports ready immediately and
    // first real use may still block. A timer does not promise a GPU time bound.
    setTimeout(check, 10);
  });
}

/** One destination render level, using the caller's existing WebGLRenderer.
 * resolveWorld(id) returns a world definition (or Promise); createWorldScene is
 * the SAME scene factory used by the playable world. No pictures or substitute
 * scenery are accepted. Cache and visible-pass limits bound GPU/memory costs.
 */
export function createPortalRenderer({renderer, createWorldScene, resolveWorld, maxVisiblePortals = 3, maxResolution = 2048, maxPixelBudget = 2 * 1024 * 1024, maxStoragePixels = 6 * 1024 * 1024, maxStorageBytes = 80 * 1024 * 1024, samples = 0, maxCachedWorlds = 6, schedulePreparation = callback => typeof globalThis.requestIdleCallback === 'function' ? globalThis.requestIdleCallback(callback, {timeout: 100}) : setTimeout(callback, 16), shouldPreloadDestination = () => true, onError = () => {}}) {
  if (!renderer || typeof createWorldScene !== 'function' || typeof resolveWorld !== 'function') throw new TypeError('Portal renderer requires a renderer, scene factory and world resolver');
  const worlds = new Map(), entries = new Map(), pending = new Map(), failures = new Map(), pinnedWorlds = new Set(), stagedUpdates = new Map();
  const size = new T.Vector2(), sampleCount = supportedPortalSamples(renderer, samples);
  const warmupTarget = new T.WebGLRenderTarget(1, 1, {type: T.HalfFloatType});
  warmupTarget.texture.colorSpace = T.LinearSRGBColorSpace;
  maxResolution = Math.max(64, Math.min(maxResolution, renderer.capabilities?.maxTextureSize ?? maxResolution));
  let active = null, activePortalId = null, serial = 0, disposed = false;
  let targetPool = [], poolKey = '', warmupQueue = Promise.resolve();
  let renderedPasses = [];
  const preparingViews = new Map();
  let viewPreparationQueue = Promise.resolve(), runningPreparations = 0;
  const preparationJobs = [], preparationLimit = Math.max(1, Math.min(2, maxCachedWorlds - 1));
  let cancelPreparations;
  const preparationDisposed = new Promise(resolve => { cancelPreparations = resolve; });
  const stats = {rendered: 0, visible: 0, loading: 0, omitted: 0, recursionDepth: 1, targetPixels: 0, aperturePixels: 0, shadowUpdates: 0, samples: sampleCount, compiling: 0, frameMilliseconds: null, cpuSubmitMilliseconds: 0, poolPixels: 0, poolBytes: 0, targetAllocations: 0, viewBuilds: 0, lastViewBuildMilliseconds: 0, incrementalUpdates: 0, warmupRenders: 0, warmupFailures: 0, warmupCpuMilliseconds: 0};
  function prepareTargetPool(viewCount = null) {
    viewCount ??= renderer.xr?.enabled && renderer.xr.isPresenting ? Math.max(1, renderer.xr.getCamera?.()?.cameras.length ?? 2) : 1;
    renderer.getDrawingBufferSize(size);
    const key = `${size.x}:${size.y}:${viewCount}`;
    if (key === poolKey || disposed) return;
    const oldTarget = renderer.getRenderTarget();
    const oldViewport = renderer.getViewport(new T.Vector4()), oldScissor = renderer.getScissor(new T.Vector4()), oldScissorTest = renderer.getScissorTest();
    const capacities = portalPoolCapacities(size.x, size.y, {count: Math.max(0, maxVisiblePortals), viewCount, maxResolution, maxStoragePixels, maxStorageBytes, samples: sampleCount});
    for (const target of targetPool) target.dispose();
    targetPool = capacities.map(({width, height}, index) => {
      const target = new T.WebGLRenderTarget(width, height, {type: T.HalfFloatType, depthBuffer: true, stencilBuffer: false, samples: sampleCount, resolveDepthBuffer: false, resolveStencilBuffer: false, minFilter: T.LinearFilter, magFilter: T.LinearFilter, generateMipmaps: false});
      target.texture.colorSpace = T.LinearSRGBColorSpace;
      target.texture.name = `Pooled portal destination ${index}`;
      target.scissorTest = true;
      return target;
    });
    try { for (const target of targetPool) renderer.initRenderTarget?.(target); }
    finally { renderer.setRenderTarget(oldTarget); renderer.setViewport(oldViewport); renderer.setScissor(oldScissor); renderer.setScissorTest(oldScissorTest); }
    poolKey = key;
    stats.targetAllocations += targetPool.length;
    stats.poolPixels = targetPool.reduce((sum, target) => sum + target.width * target.height, 0);
    stats.poolBytes = capacities.reduce((sum, capacity) => sum + capacity.bytes, 0);
  }
  function releaseEntry(entry) {
    entry.released=true;
    const release = () => {
      for (const item of entry.portals) { disposeObject(item.surface); if (item.frame) disposeObject(item.frame); item.label?.userData.dispose(); }
      entry.view.dispose?.();
    };
    // compileAsync polls each material's program; do not destroy it mid-poll.
    if (entry.warmup) void entry.warmup.finally(release); else release();
    if (entries.get(entry.world.id) === entry) entries.delete(entry.world.id);
  }
  function trimCache(protectedIds = new Set()) {
    while (entries.size > Math.max(2, maxCachedWorlds)) {
      const candidates = [...entries.values()].filter(entry => entry !== active && !pinnedWorlds.has(entry.world.id) && !protectedIds.has(entry.world.id) && !preparingViews.has(entry.world.id)).sort((a, b) => a.used - b.used);
      if (!candidates.length) break;
      releaseEntry(candidates[0]);
    }
  }
  function deferredRelease(entry, release) {
    if (entry.warmup) void entry.warmup.finally(release); else release();
  }
  function releasePortal(entry, item) {
    item.surface.removeFromParent(); item.frame?.removeFromParent(); item.label?.removeFromParent();
    deferredRelease(entry, () => { disposeObject(item.surface); if (item.frame) disposeObject(item.frame); item.label?.userData.dispose(); });
  }
  const portalShapeKey = portal => JSON.stringify([portal.width ?? DEFAULT_WIDTH, portal.height ?? DEFAULT_HEIGHT, portal.aperture ?? 'rectangle']);
  function syncPortals(entry) {
    const portals = selectedPortals(entry.world, entry === active ? activePortalId : undefined);
    const key = JSON.stringify(portals);
    if (entry.portalKey === key) return false;
    const previous = new Map(entry.portals.map(item => [item.portal.id, item]));
    entry.portals = portals.map(definition => {
      const portal = structuredClone(definition);
      let item = previous.get(portal.id);
      previous.delete(portal.id);
      if (!item) {
        item = createSurface(portal);
        entry.view.root.add(item.surface);
        if (item.label) entry.view.root.add(item.label);
      } else if (JSON.stringify(item.portal) !== JSON.stringify(portal)) {
        const oldShape = portalShapeKey(item.portal), nextShape = portalShapeKey(portal);
        if (oldShape !== nextShape) {
          const oldGeometry = item.surface.geometry;
          item.surface.geometry = createPortalApertureGeometry(portal); item.apertureKey = '';
          deferredRelease(entry, () => oldGeometry.dispose());
          if (item.frame) { const frame = item.frame; frame.removeFromParent(); deferredRelease(entry, () => disposeObject(frame)); item.frame = null; }
        }
        if ((item.portal.frame ?? 'visible') !== (portal.frame ?? 'visible') && item.frame) {
          const frame = item.frame; frame.removeFromParent(); deferredRelease(entry, () => disposeObject(frame)); item.frame = null;
        }
        item.portal = portal;
        item.surface.matrix.copy(portalFrameMatrix(portal)); item.surface.matrixWorldNeedsUpdate = true;
        item.surface.name = `Live portal · ${portal.id} → ${portal.targetWorldId}`;
        // Never show a previously rendered destination after reconnecting a door.
        item.surface.visible = false; setStatus(item, 'loading');
        if (item.label) item.label.position.set(portal.position.x, portal.position.y + (portal.height ?? DEFAULT_HEIGHT) + .5, portal.position.z);
      }
      const embeddedFrame = portal.frame === 'none' || entry.view.hasPortalFrame?.(portal);
      if (embeddedFrame && item.frame) { const frame = item.frame; frame.removeFromParent(); deferredRelease(entry, () => disposeObject(frame)); item.frame = null; }
      if (!embeddedFrame && !item.frame) { item.frame = createPortalFrame(portal); entry.view.root.add(item.frame); }
      if (item.frame) { item.frame.matrix.copy(portalFrameMatrix(portal)); item.frame.matrixWorldNeedsUpdate = true; }
      return item;
    });
    for (const item of previous.values()) releasePortal(entry, item);
    entry.portalKey = key;
    entry.lastShadowUpdate = -Infinity;
    entry.warmed = false; entry.preparationSettled = false; entry.retryWarmupAt = 0;
    return true;
  }
  function makeEntry(world, view, previous) {
    const started = performance.now(), worldView = view ?? createWorldScene(world, {time: previous?.view.getElapsed?.() ?? 0});
    if (!view) { stats.viewBuilds++; stats.lastViewBuildMilliseconds = performance.now() - started; }
    const entry = {world, revision: world.revision, view: worldView, portals: [], portalKey: '', used: ++serial, lastShadowUpdate: -Infinity, warmed: false, preparationSettled: false, warmup: null, retryWarmupAt: 0};
    worldView.restoreAnimationFrom?.(previous?.view);
    syncPortals(entry);
    return entry;
  }
  function commitEntry(entry, existing) {
    const wasActive = existing && existing === active;
    // Stage completely before releasing the old scene/resources. No partial
    // world map is observable during construction or compiler preparation.
    entries.set(entry.world.id, entry); worlds.set(entry.world.id, entry.world);
    if (wasActive) active = entry;
    if (existing) releaseEntry(existing);
    if (wasActive) syncPortals(entry);
    return entry.view;
  }
  function updateEntry(existing, world) {
    const deferDispose = release => deferredRelease(existing, release);
    if (existing.view.updateWorld?.(world, {deferDispose}) !== true) return false;
    existing.world = world; existing.revision = world.revision; existing.used = ++serial;
    existing.lastShadowUpdate = -Infinity; existing.warmed = false; existing.preparationSettled = false; existing.retryWarmupAt = 0;
    worlds.set(world.id, world); syncPortals(existing); stats.incrementalUpdates++;
    return true;
  }
  function registerWorld(world, view) {
    if (disposed) throw new Error('Portal renderer is disposed');
    const existing = entries.get(world.id);
    // Keep the admitted revision snapshot separate from mutable local JSON.
    if (existing && existing.revision > world.revision) return existing.view;
    if (existing && existing.revision === world.revision && (!view || view === existing.view)) {
      existing.world = world; existing.used = ++serial; worlds.set(world.id, world); syncPortals(existing); return existing.view;
    }
    stagedUpdates.delete(world.id);
    if (existing && (!view || view === existing.view) && updateEntry(existing, world)) return existing.view;
    return commitEntry(makeEntry(world, view, existing), existing);
  }
  async function updateWorld(world, {isCurrent = () => true} = {}) {
    if (disposed) throw new Error('Portal renderer is disposed');
    const existing = entries.get(world.id);
    if (!isCurrent() || (existing && existing.revision >= world.revision)) return existing?.view ?? null;
    const previous = stagedUpdates.get(world.id);
    let finish;
    const ticket = {finished: new Promise(resolve => { finish = resolve; })}; stagedUpdates.set(world.id, ticket);
    const current = () => !disposed && stagedUpdates.get(world.id) === ticket && isCurrent() && entries.get(world.id) === existing;
    let staged = null;
    try {
      // An in-flight compiler must finish reading materials before an entity
      // revision releases/replaces them. The previous world stays usable.
      // Serialize expensive replacements for the same world. A rapid stream
      // of revisions can keep at most one staged environment alive per ID.
      if (previous) await previous.finished;
      if (!current()) return entries.get(world.id)?.view ?? null;
      if (existing?.warmup) await existing.warmup;
      if (!current()) return entries.get(world.id)?.view ?? null;
      if (existing?.view.canUpdateWorld?.(world)) {
        if (updateEntry(existing, world)) { void warmEntry(existing); return existing.view; }
      }
      staged = makeEntry(world, null, existing);
      await warmEntry(staged);
      // Safety/revision preparation can be superseded while compiling. Never
      // publish old data or dispose the winning view when that happens.
      if (!current()) { releaseEntry(staged); staged = null; return entries.get(world.id)?.view ?? null; }
      staged.view.restoreAnimationFrom?.(existing?.view);
      return commitEntry(staged, existing);
    } catch (error) { if (staged) releaseEntry(staged); throw error; }
    finally { if (stagedUpdates.get(world.id) === ticket) stagedUpdates.delete(world.id); finish(); }
  }
  function ensureView(world) {
    const existing = entries.get(world.id);
    // Definitions are immutable snapshots between admissions. Avoid serializing
    // every portal and touching the scene graph again on every render pass.
    if (existing?.world === world && existing.revision === world.revision) return existing;
    registerWorld(world); return entries.get(world.id);
  }
  function pumpPreparations() {
    while (preparationJobs.length && runningPreparations < preparationLimit) {
      const {world, finish} = preparationJobs.shift();
      runningPreparations++;
      // Only construction occupies this queue. Waiting for one world's assets
      // must not prevent another visible destination from becoming usable.
      const built = viewPreparationQueue.then(async () => {
        await Promise.race([new Promise(resolve => schedulePreparation(resolve)), preparationDisposed]);
        if (disposed) return null;
        const entry = ensureView(worlds.get(world.id) ?? world);
        trimCache();
        return entry;
      });
      viewPreparationQueue = built.catch(() => {});
      void built.then(async entry => {
        if (!entry || disposed || entry.released) return;
        entry.view.entities?.retryModels?.();
        if (entry.view.entities?.modelsReady === false) {
          await Promise.race([entry.view.entities.ready, preparationDisposed]);
        }
        if (disposed || entry.released) return;
        // Asset waits run independently (at most two preparations); shader and
        // geometry work shares the existing serialized visual-warmup queue.
        const warmed = warmupQueue.then(() => warmEntry(entry));
        warmupQueue = warmed.catch(() => {});
        await warmed;
      }).catch(error => { if (!disposed) onError(error, world.id); }).finally(() => {
        preparingViews.delete(world.id);
        runningPreparations--;
        if (!disposed) trimCache(new Set([world.id]));
        finish();
        pumpPreparations();
      });
    }
  }
  function prepareView(world) {
    if (disposed) return Promise.resolve();
    if (preparingViews.has(world.id)) return preparingViews.get(world.id);
    let finish;
    const pending = new Promise(resolve => { finish = resolve; });
    preparingViews.set(world.id, pending);
    preparationJobs.push({world, finish});
    pumpPreparations();
    return pending;
  }
  function warmupObject(request) {
    const run = async () => {
      const {object, sourceScene, targetScene, withMaterials, isValid = () => true} = request;
      if (!object || !withMaterials || disposed || !isValid()) return;
      // This uses actual render calls: compileAsync alone does not initialize
      // WebGLClipping's material-specific plane counts or shadow depth variants.
      for (const reference of new Set([sourceScene, targetScene])) {
        if (!reference || disposed || !isValid()) continue;
        const stage = new T.Scene(), lights = [];
        stage.fog = reference.fog; stage.environment = reference.environment;
        stage.environmentIntensity = reference.environmentIntensity;
        stage.environmentRotation.copy(reference.environmentRotation);
        reference.updateMatrixWorld();
        reference.traverseVisible(light => {
          if (!light.isLight) return;
          const copy = light.clone(false);
          copy.matrix.copy(light.matrixWorld); copy.matrix.decompose(copy.position, copy.quaternion, copy.scale);
          if (copy.shadow) copy.shadow.mapSize.set(4, 4);
          if (copy.target) stage.add(copy.target);
          stage.add(copy); lights.push(copy);
        });
        try {
          for (const side of ['base', 'source', 'target']) for (const output of ['hdr', 'display']) {
            await new Promise(resolve => setTimeout(resolve, 0));
            if (disposed || !isValid()) return;
            const started = performance.now();
            const oldTarget = renderer.getRenderTarget();
            const oldViewport = renderer.getViewport(new T.Vector4()), oldScissor = renderer.getScissor(new T.Vector4()), oldScissorTest = renderer.getScissorTest();
            const oldXr = renderer.xr.enabled, oldShadows = renderer.shadowMap.autoUpdate, oldShadowNeeds = renderer.shadowMap.needsUpdate;
            const oldAutoClear = renderer.autoClear, oldClipping = renderer.localClippingEnabled;
            const parent = object.parent, index = parent?.children.indexOf(object), visible = object.visible;
            const culling = [];
            object.traverse(child => { culling.push([child, child.frustumCulled, child.visible]); child.frustumCulled = false; child.visible = true; });
            const camera = new T.PerspectiveCamera(60, 1, .08, 700);
            const position = new T.Vector3().setFromMatrixPosition(object.matrixWorld);
            camera.position.copy(position).add(new T.Vector3(0, 1, 5)); camera.lookAt(position); camera.layers.enableAll(); camera.updateMatrixWorld();
            try {
              renderer.xr.enabled = false; renderer.localClippingEnabled = true; renderer.autoClear = true;
              renderer.shadowMap.autoUpdate = output === 'hdr'; renderer.shadowMap.needsUpdate = output === 'hdr';
              stage.add(object); object.visible = true;
              withMaterials(side, () => {
                if (output === 'hdr') { renderer.setRenderTarget(warmupTarget); renderer.setScissorTest(false); }
                else {
                  // A zero-sized scissor submits real display shader variants
                  // while protecting every pixel of the user's framebuffer.
                  renderer.setRenderTarget(null); renderer.setViewport(new T.Vector4(0, 0, 1, 1));
                  renderer.setScissor(new T.Vector4(0, 0, 0, 0)); renderer.setScissorTest(true);
                }
                renderer.render(stage, camera); stats.warmupRenders++;
              });
            } finally {
              object.removeFromParent(); object.visible = visible;
              for (const [child, frustumCulled, visible] of culling) { child.frustumCulled = frustumCulled; child.visible = visible; }
              if (parent) { parent.add(object); parent.children.splice(parent.children.indexOf(object), 1); parent.children.splice(index, 0, object); }
              renderer.setRenderTarget(oldTarget); renderer.setViewport(oldViewport); renderer.setScissor(oldScissor); renderer.setScissorTest(oldScissorTest);
              renderer.xr.enabled = oldXr; renderer.shadowMap.autoUpdate = oldShadows; renderer.shadowMap.needsUpdate = oldShadowNeeds;
              renderer.autoClear = oldAutoClear; renderer.localClippingEnabled = oldClipping;
              stats.warmupCpuMilliseconds += performance.now() - started;
            }
          }
        } finally { for (const light of lights) light.shadow?.dispose(); stage.clear(); }
      }
    };
    const pending = warmupQueue.then(run);
    warmupQueue = pending.catch(error => { onError(error, 'visual-warmup'); });
    return pending;
  }
  function warmEntry(entry) {
    if(disposed||entry.released)return Promise.resolve();
    entry.view.entities?.retryModels?.();
    if(entry.view.entities?.modelErrors?.length)return Promise.resolve();
    if(entry.view.entities?.modelsReady===false){if(entry.modelsWarmup)return entry.modelsWarmup;entry.modelsWarmup=entry.view.entities.ready.then(()=>{if(disposed||entry.released)return;if(entry.view.entities.modelsReady===false)throw Error('Destination model assets failed to load');entry.modelsWarmup=null;return warmEntry(entry);}).catch(error=>{entry.modelsWarmup=null;if(!disposed&&!entry.released)onError(error,entry.world.id);});return entry.modelsWarmup;}
    if (entry.warmed || typeof renderer.compileAsync !== 'function') return Promise.resolve();
    if (entry.warmup) return entry.warmup;
    if (Date.now() < entry.retryWarmupAt) return Promise.resolve();
    const oldTarget = renderer.getRenderTarget();
    const oldViewport = renderer.getViewport(new T.Vector4()), oldScissor = renderer.getScissor(new T.Vector4()), oldScissorTest = renderer.getScissorTest();
    const camera = new T.PerspectiveCamera(60, 1, .08, 700), programs = [], revision = entry.revision, portalKey = entry.portalKey;
    let preparationSettled = false;
    stats.compiling++;
    try {
      // Compile both output variants and snapshot their exact program objects.
      // Compilation setup is synchronous; restore state BEFORE awaiting polls.
      renderer.setRenderTarget(warmupTarget);
      programs.push(compilePortalProgramsReady(renderer, entry.view.scene, camera));
      renderer.setRenderTarget(null);
      programs.push(compilePortalProgramsReady(renderer, entry.view.scene, camera));
      if (renderer.initTexture) {
        const textures = new Set();
        entry.view.scene.traverse(object => {
          for (const material of object.material ? (Array.isArray(object.material) ? object.material : [object.material]) : []) {
            for (const value of Object.values(material)) if (value?.isTexture && !value.isRenderTargetTexture) textures.add(value);
          }
        });
        for (const texture of textures) renderer.initTexture(texture);
      }
    } catch (error) { programs.push(Promise.reject(error)); }
    finally {
      renderer.setRenderTarget(oldTarget); renderer.setViewport(oldViewport); renderer.setScissor(oldScissor); renderer.setScissorTest(oldScissorTest);
    }
    entry.warmup = Promise.allSettled(programs).then(results => {
      // A rejected variant must not mark preparation settled while another
      // compiler still owns the scene/materials. Preserve fallback afterward.
      const failure = results.find(result => result.status === 'rejected');
      if (failure) throw failure.reason;
      if (disposed || entry.released || entry.revision !== revision || entry.portalKey !== portalKey) return;
      // compileAsync only creates programs. A tiny real draw also initializes
      // vertex/index buffers and shadow targets before an eye/body handoff.
      const target = renderer.getRenderTarget(), viewport = renderer.getViewport(new T.Vector4());
      const scissor = renderer.getScissor(new T.Vector4()), scissorTest = renderer.getScissorTest();
      const xr = renderer.xr.enabled, autoClear = renderer.autoClear;
      const autoShadows = renderer.shadowMap.autoUpdate, needsShadows = renderer.shadowMap.needsUpdate;
      const objects = [], labels = new Set(entry.portals.map(portal => portal.label)), started = performance.now();
      entry.view.scene.traverse(object => {
        objects.push([object, object.frustumCulled, object.visible]);
        object.frustumCulled = false;
        if (object.userData.isPortalSurface || labels.has(object)) object.visible = false;
      });
      try {
        renderer.xr.enabled = false; renderer.autoClear = true;
        renderer.shadowMap.autoUpdate = true; renderer.shadowMap.needsUpdate = true;
        renderer.setRenderTarget(warmupTarget); renderer.setViewport(new T.Vector4(0, 0, 1, 1)); renderer.setScissorTest(false);
        renderer.render(entry.view.scene, camera); stats.warmupRenders++;
        entry.warmed = true; preparationSettled = true;
      } finally {
        for (const [object, culled, visible] of objects) { object.frustumCulled = culled; object.visible = visible; }
        renderer.setRenderTarget(target); renderer.setViewport(viewport); renderer.setScissor(scissor); renderer.setScissorTest(scissorTest);
        renderer.xr.enabled = xr; renderer.autoClear = autoClear;
        renderer.shadowMap.autoUpdate = autoShadows; renderer.shadowMap.needsUpdate = needsShadows;
        stats.warmupCpuMilliseconds += performance.now() - started;
      }
    }).catch(error => {
      // A failed preparation retains the existing usable render fallback. It
      // must not strand the current world behind the arrival/loading gate.
      preparationSettled = true; stats.warmupFailures++;
      if (entry.revision === revision && entry.portalKey === portalKey) entry.retryWarmupAt = Date.now() + 5000;
      onError(error, entry.world.id);
    }).finally(() => {
      stats.compiling--; entry.warmup = null;
      // A late completion cannot admit a newer revision or portal layout.
      if (!disposed && !entry.released && entry.revision === revision && entry.portalKey === portalKey) entry.preparationSettled = preparationSettled;
    });
    return entry.warmup;
  }
  async function loadWorld(id) {
    if (worlds.has(id)) return worlds.get(id);
    if (pending.has(id)) return pending.get(id);
    if ((failures.get(id) ?? 0) > Date.now()) return null;
    const request = Promise.resolve().then(() => resolveWorld(id)).then(world => {
      if (!world?.id || world.id !== id) throw new Error(`Portal destination ${id} is unavailable`);
      const known = worlds.get(id);
      const latest = known?.revision > world.revision ? known : world;
      if (!disposed) worlds.set(id, latest);
      failures.delete(id);
      return latest;
    }).catch(error => { failures.set(id, Date.now() + 5000); onError(error, id); return null; }).finally(() => pending.delete(id));
    pending.set(id, request);
    return request;
  }
  async function preload(world = active?.world, {waitForDeferred = false} = {}) {
    if (!world || disposed) return [];
    prepareTargetPool();
    const known = worlds.get(world.id);
    if (known?.revision > world.revision) world = known;
    else worlds.set(world.id, world);
    const ids = [...new Set(selectedPortals(world, activePortalId).map(portal => portal.targetWorldId))];
    const loaded = (await Promise.all(ids.map(loadWorld))).filter(Boolean);
    if (disposed) return [];
    const warm = [];
    const sourceEntry = entries.get(world.id);
    if (sourceEntry) warm.push(warmEntry(sourceEntry));
    const neighbors = loaded.slice(0, Math.max(1, maxCachedWorlds - 1));
    for (const destination of neighbors.filter(shouldPreloadDestination)) warm.push(warmEntry(ensureView(destination)));
    trimCache();
    await Promise.all(warm);
    // Runtime preloads schedule large destinations outside render. Initial
    // arrival can join these same bounded jobs before releasing controls,
    // including an existing view whose model/visual preparation is still pending.
    const deferred = neighbors.filter(destination => !shouldPreloadDestination(destination) && (waitForDeferred || !entries.has(destination.id))).map(destination => prepareView(destination));
    if (waitForDeferred) await Promise.all(deferred);
    return loaded;
  }
  function setWorld(world, view, options = {}) {
    activePortalId = options.activePortalId ?? null;
    const result = registerWorld(world, view);
    active = entries.get(world.id);
    syncPortals(active);
    void preload(world);
    trimCache();
    return result;
  }
  function render(camera, {dt = 0, frameTime = dt, time = 0, skipPortalIds = [], activePortalId: preferred = activePortalId, cameraCrossing = null, portalTraversal = null} = {}) {
    renderedPasses = [];
    const started = performance.now();
    Object.assign(stats, {rendered: 0, visible: 0, omitted: 0, loading: pending.size + preparingViews.size, targetPixels: 0, aperturePixels: 0, shadowUpdates: 0, cpuSubmitMilliseconds: 0, frameMilliseconds: frameTime > 0 && frameTime <= .25 ? frameTime * 1000 : null});
    if (!active || disposed) return {...stats};
    if (preferred !== activePortalId) { activePortalId = preferred; syncPortals(active); void preload(); }
    camera.updateMatrixWorld(); active.view.scene.updateMatrixWorld();
    const views = portalViewCameras(renderer, camera);
    prepareTargetPool(Math.max(1, views.length));
    const skipped = new Set(skipPortalIds), candidates = [];
    for (const item of active.portals) {
      item.surface.visible = false;
      resetPortalEyeSamples(item.surface);
      if (skipped.has(item.portal.id)) continue;
      const pair = resolvePortalPair(active.world, item.portal, worlds);
      const aperture = commonPortalAperture(item.portal, pair?.target), key = JSON.stringify(aperture);
      if (key !== item.apertureKey) { item.surface.geometry.dispose(); item.surface.geometry = createPortalApertureGeometry(aperture); item.apertureKey = key; }
      const eyePasses = views.map(view => {
        const viewport = portalViewSize(view, size.x, size.y);
        return {camera: view, bounds: projectedPortalBounds({...item.portal, ...aperture}, view, viewport.width, viewport.height)};
      }).filter(pass => pass.bounds);
      if (!eyePasses.length) continue;
      if (!worlds.has(item.portal.targetWorldId)) {
        setStatus(item, failures.has(item.portal.targetWorldId) ? 'unavailable' : 'loading');
        void loadWorld(item.portal.targetWorldId);
      } else if (!pair) setStatus(item, 'unavailable');
      candidates.push({item, pair, eyePasses, score: eyePasses.reduce((sum, pass) => sum + pass.bounds.area, 0)});
    }
    candidates.sort((a, b) => b.score - a.score);
    stats.visible = candidates.length;
    const renderable = candidates.filter(candidate => candidate.pair);
    stats.omitted = Math.max(0, renderable.length - maxVisiblePortals);
    const selected = renderable.slice(0, Math.max(0, maxVisiblePortals)).flatMap(({item, pair, eyePasses}, portalIndex) =>
      eyePasses.map(pass => ({item, pair, ...pass, poolIndex: portalIndex * views.length + (pass.camera.userData.valoriaPortalEyeIndex ?? 0)})));
    const budget = Math.max(4 * selected.length, maxPixelBudget);
    const requestedPixels = selected.reduce((sum, {bounds}) => sum + bounds.pixelWidth * bounds.pixelHeight, 0);
    const scale = Math.min(1, Math.sqrt(budget / Math.max(1, requestedPixels)));
    const plan = density => selected.map(({bounds, poolIndex}) => {
      const capacity = targetPool[poolIndex];
      const size = portalTargetSize(bounds, {scale: Math.min(density, capacity.width / bounds.pixelWidth, capacity.height / bounds.pixelHeight), maxResolution});
      return {width: Math.min(capacity.width, size.width), height: Math.min(capacity.height, size.height)};
    });
    let allocations = plan(scale);
    const totalPixels = sizes => sizes.reduce((sum, size) => sum + size.width * size.height, 0);
    // Bucket padding and retained allocation headroom must also obey the budget.
    // Under pressure shrink immediately rather than letting hysteresis evade it.
    if (totalPixels(allocations) > budget) {
      let low = 0, high = scale;
      for (let i = 0; i < 16; i++) {
        const trial = (low + high) / 2;
        const sizes = plan(trial);
        if (totalPixels(sizes) <= budget) low = trial; else high = trial;
      }
      allocations = plan(low);
    }
    const oldTarget = renderer.getRenderTarget();
    const oldViewport = renderer.getViewport(new T.Vector4()), oldScissor = renderer.getScissor(new T.Vector4()), oldScissorTest = renderer.getScissorTest();
    const oldXr = renderer.xr.enabled, oldShadows = renderer.shadowMap.autoUpdate, oldShadowNeeds = renderer.shadowMap.needsUpdate;
    const oldAutoClear = renderer.autoClear;
    const protectedIds = new Set([active.world.id]);
    const updated = new Set(), reported = new Set();
    try {
      renderer.xr.enabled = false;
      renderer.autoClear = true;
      renderer.setScissorTest(false);
      for (let index = 0; index < selected.length; index++) {
        const {item, pair, bounds, camera: viewCamera, poolIndex} = selected[index], {width, height} = allocations[index];
        const target = targetPool[poolIndex];
        const destination = pair.world;
        const entry = entries.get(destination.id);
        if (!entry || entry.revision < destination.revision) {
          setStatus(item, 'loading'); void prepareView(destination); continue;
        }
        if (preparingViews.has(destination.id)) { setStatus(item, 'loading'); continue; }
        if(entry.view.entities?.modelsReady===false){setStatus(item,entry.view.entities.modelErrors?.length?'unavailable':'loading');void warmEntry(entry);continue;}
        // Metadata/presence paths can admit a view before preload reaches it.
        // Prepare that same view off-frame; never rebuild it in a crossing.
        if (typeof renderer.compileAsync === 'function' && !entry.preparationSettled) { setStatus(item, 'loading'); void prepareView(destination); continue; }
        protectedIds.add(destination.id); entry.used = ++serial;
        if (entry !== active && !updated.has(entry)) { entry.view.update?.(dt, time); updated.add(entry); }
        // Viewport/scissor can change every frame without reallocating textures,
        // depth attachments or multisample storage. The pool survives crossings.
        target.viewport.set(0, 0, width, height); target.scissor.copy(target.viewport);
        // A camera-first arm can look back at a body still in the actual entry
        // world. This one live return membrane must undo that arm's exact frame;
        // the receipt also preserves that inverse on subsequent reciprocal visits.
        const reverseArm = cameraCrossing?.world?.id === active.world.id &&
          cameraCrossing.target?.id === item.portal.id && cameraCrossing.source?.id === pair.target.id &&
          cameraCrossing.sourceWorldId === destination.id;
        configurePortalCamera(viewCamera, item.portal, pair.target, item.camera, {crop: bounds,
          ...(reverseArm ? {sourceSide: cameraCrossing.targetSide, targetSide: cameraCrossing.sourceSide} : {targetSide:portalDestinationSide(active.world,pair,portalApproachSide(viewCamera.getWorldPosition(new T.Vector3()),item.portal),portalTraversal)})});
        // Deliberately one live level. Hide ALL destination membranes, including
        // a self-world pair, to prevent read/write feedback and unbounded render.
        const visibility = entry.portals.flatMap(portal => [portal.surface, portal.label].filter(Boolean).map(object => [object, object.visible]));
        for (const [surface] of visibility) surface.visible = false;
        try {
          renderer.shadowMap.autoUpdate = time - entry.lastShadowUpdate > .12;
          renderer.shadowMap.needsUpdate = renderer.shadowMap.autoUpdate;
          renderer.setRenderTarget(target);
          renderer.render(entry.view.scene, item.camera);
          if (renderer.shadowMap.autoUpdate) { entry.lastShadowUpdate = time; stats.shadowUpdates++; }
          stats.rendered++;
          setPortalEyeSample(item.surface, viewCamera, {texture: target.texture, bounds, width, height, targetWidth: target.width, targetHeight: target.height});
          if (!reported.has(item)) {
            renderedPasses.push({worldId: active.world.id, portalId: item.portal.id, targetWorldId: destination.id});
            reported.add(item);
          }
          stats.targetPixels += width * height; stats.aperturePixels += bounds.area;
          setStatus(item, 'ready');
        } catch (error) { setStatus(item, 'unavailable'); onError(error, destination.id); continue; } finally { for (const [surface, visible] of visibility) surface.visible = visible; }
        item.surface.visible = true;
      }
    } finally {
      renderer.setRenderTarget(oldTarget);
      renderer.setViewport(oldViewport); renderer.setScissor(oldScissor); renderer.setScissorTest(oldScissorTest);
      renderer.xr.enabled = oldXr; renderer.shadowMap.autoUpdate = oldShadows; renderer.shadowMap.needsUpdate = oldShadowNeeds;
      renderer.autoClear = oldAutoClear;
    }
    trimCache(protectedIds);
    stats.loading = pending.size + preparingViews.size;
    stats.cpuSubmitMilliseconds = performance.now() - started;
    return {...stats};
  }
  return {
    registerWorld, updateWorld, setWorld, preload, render, warmupObject,
    invalidateShadows(id) { const entry = entries.get(id); if (entry) entry.lastShadowUpdate = -Infinity; },
    setPinnedWorlds(ids = []) { pinnedWorlds.clear(); for (const id of ids) pinnedWorlds.add(id); },
    getWorld: id => worlds.get(id),
    isReady: id => entries.has(id)&&!preparingViews.has(id)&&entries.get(id).view.entities?.modelsReady!==false&&(typeof renderer.compileAsync!=='function'||entries.get(id).preparationSettled),
    getStatus: id => preparingViews.has(id) ? 'loading' : entries.has(id) ? (entries.get(id).view.entities?.modelErrors?.length?'unavailable':entries.get(id).view.entities?.modelsReady===false||typeof renderer.compileAsync==='function'&&!entries.get(id).preparationSettled?'loading':'ready') : pending.has(id) ? 'loading' : failures.has(id) ? 'unavailable' : worlds.has(id) ? 'loaded' : 'unknown',
    getWorldView: id => entries.get(id)?.view,
    getWorlds: () => worlds,
    getSurfaces: () => active?.portals.map(item => item.surface) ?? [],
    // Metadata from the most recently completed frame, not a second visibility
    // algorithm. Read-only presence uses exactly the doorways actually drawn.
    getRenderedPasses: () => renderedPasses.map(pass => ({...pass})),
    getStats: () => ({...stats}),
    dispose() {
      if (disposed) return;
      disposed = true;
      cancelPreparations();
      for (const {world, finish} of preparationJobs.splice(0)) { preparingViews.delete(world.id); finish(); }
      const warmups = [...entries.values()].map(entry => entry.warmup).filter(Boolean);
      for (const entry of [...entries.values()]) releaseEntry(entry);
      for (const target of targetPool) target.dispose(); targetPool = [];
      void Promise.allSettled([...warmups, warmupQueue]).then(() => warmupTarget.dispose());
      worlds.clear(); stagedUpdates.clear(); active = null;
    },
  };
}

